import React from "react";

const Article = () => {
  return (
    <div className="mb-20">
      <div>
        <h2 id="HGT_h">Horizontal Gene Transfer </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-5">
          While conducting our experiments we needed to check that our bacteria
          had integrated a plasmid, that it has a Uplasmid and not a simple plasmid,
          and show that if it has a Uplasmid it could express the protein.
          Finally, we aimed to establish that mutations in the DUT/UNG gene are responsible
          for the observed genetic alterations.
        </p>
        <p className="mb-5">
        </p>
        <h2 className="mt-20" id="plasmid_h"> Checking that the strain has integrated a plasmid </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-20">
          To confirm the successful transformation of our bacteria, we employed a growth
          assay on agar plates containing Ampicillin. Since our plasmid carries a gene that imparts resistance
          to Ampicillin, the growth of bacteria on these plates serves as evidence of successful plasmid insertion.
          We have effectively achieved this outcome, as detailed in the <a href="../epfl/results#Results_hgt">Results section</a>.
        </p>

        <figure>
          <img
            id="Figure1"
            src="https://static.igem.wiki/teams/4888/wiki/experiments/agar-plate.png"
            alt="Experiment_SpaC"
            class="centered-image"
            style={{ width: "90%" }}
          />
          <br></br>
          <figcaption>
            <b>
              <u>Figure 1:</u>{" "}
            </b>
            Schematic of the bacteria culture on agar plate + ampicillin
          </figcaption>
        </figure>
        <br></br>
        <center>
          <details>
            <summary id="Western_Blot_protocol"><mark><b>Autoclave</b></mark></summary>
            <object data=
              "https://static.igem.wiki/teams/4888/wiki/prots/4.pdf"
              width="800"
              height="500"
              aria-label="western blot">
            </object>
          </details>
        </center>
        <center>
          <details>
            <summary id="Western_Blot_protocol"><mark><b>Pour Agarose plates</b></mark></summary>
            <object data=
              "https://static.igem.wiki/teams/4888/wiki/prots/6.pdf"
              width="800"
              height="500"
              aria-label="western blot">
            </object>
          </details>
        </center>
        <h2 className="mt-20" id="uplasmid_h"> Checking that the plasmid is a Uplasmid  </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-2">
          After establishing that our bacteria have undergone plasmid
          transformation, our next objective was to confirm whether this plasmid is a Uplasmid.
          According to existing literature, bacterial strains lacking at least the UNG mutations
          will degrade uracilated plasmids (Uplasmids). Consequently, when these bacteria are
          placed on Ampicillin plates, they will die as the plasmid containing Ampicillin resistance
          is degraded.
        </p><p className="mb-20">
          To address this, we grew strains known to possess both UNG
          mutations and those that do not. By comparing the viability of cells with these mutations
          to those without, we observed a substantial difference, with the mutated strains showing
          significantly higher survival rates. This outcome leads us to the conclusion that the transformed
          bacteria carry a Uplasmid rather than a simple plasmid, as detailed in the
          <a href="../epfl/results#Results_hgt"> Results section</a>.
        </p>
        <figure>
          <img
            id="Figure2"
            src="https://static.igem.wiki/teams/4888/wiki/hgt/hgt3.png"
            alt="Experiment_SpaC"
            class="centered-image"
            style={{ width: "90%" }}
          />
          <br></br>
          <figcaption>
            <b>
              <u>Figure 2:</u>{" "}
            </b>
            Schematic of the bacteria <i>E.Coli CJ236</i> incorporating Uracil
          </figcaption>
        </figure>
        <figure>
          <img
            id="Figure3"
            src="https://static.igem.wiki/teams/4888/wiki/hgt/hgt4.png"
            alt="Experiment_SpaC"
            class="centered-image"
            style={{ width: "30%" }}
          />
          <br></br>
          <figcaption>
            <b>
              <u>Figure 3:</u>{" "}
            </b>
            A Uplasmid
          </figcaption>
        </figure>
        <br></br>
        <center>
          <details>
            <summary id="Western_Blot_protocol"><mark><b>Transformation</b></mark></summary>
            <object data=
              "https://static.igem.wiki/teams/4888/wiki/prots/2.pdf"
              width="800"
              height="500"
              aria-label="western blot">
            </object>
          </details>
        </center>
        <h2 className="mt-20" id="protein_h"> Checking that the protein in the Uplasmid is expressed  </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-20">
          Having confirmed that our plasmid is indeed a Uplasmid, the next step was to determine whether
          its transformation into a viable bacterial strain still resulted in protein production. In our
          experimental setup, the presence of Arabinose is crucial for the synthesis of GFP (Green
          Fluorescent Protein). Therefore, we incorporated Arabinose into our agar solution when
          preparing our plates. To confirm the successful expression of GFP, we used fluorescent microscope
          equipped to detect GFP fluorescence. We successfully carried out these experiments and
          obtained the expected results, as outlined in the <a href="../epfl/results#Results_hgt">Results section</a>.
        </p>
        <figure>
          <img
            id="Figure4"
            src="https://static.igem.wiki/teams/4888/wiki/hgt/hgt5.png"
            alt="Experiment_SpaC"
            class="centered-image"
            style={{ width: "60%" }}
          />
          <br></br>
          <figcaption>
            <b>
              <u>Figure 4:</u>{" "}
            </b>
            Viability of Uplasmid in different <i>E.Coli</i> strains
          </figcaption>
        </figure>
        <br></br>
        <center>
          <details>
            <summary id="Western_Blot_protocol"><mark><b>Ampicilin Arabinose plates</b></mark></summary>
            <object data=
              "https://static.igem.wiki/teams/4888/wiki/prots/29.pdf"
              width="800"
              height="500"
              aria-label="western blot">
            </object>
          </details>
        </center>
        <h2 className="mt-20" id="mutations_h"> Checking that the DUT/UNG mutations are necessary for Uplasmid
          production and preventing HGT </h2>
        <hr className="dividergeneral mb-5"></hr>
        <p className="mb-5">
          To ensure that the double mutations are indeed responsible for the Uplasmid and, subsequently, for
          preventing Horizontal Gene Transfer (H.G.T.), we decided to make a substitution. Instead of
          culturing CJ236 with dUTP, we replaced this strain with DH5a. Consequently, when harvesting the
          plasmid to be used for the transformation of different strains, the results on the plates should
          exhibit a relatively consistent number of colonies across all strains, regardless of whether they
          possess the double mutations or not. This observation could suggest that a simple plasmid was used
          for transformation rather than a Uplasmid. We successfully conducted this experiment, and the
          findings are detailed in the <a href="../epfl/results#Results_hgt">Results section</a>.
        </p>
        <br></br>
        <p>
          <br></br>
          Note: Our choice of incubation times of 20 and 30 minutes was influenced by previous research. 
          When similar bacteria were grown for a period of 2 hours in a comparable 
          medium, at temperatures ranging from 37°C to 42°C, there is a substantial 91% 
          replacement of thymine (T) with uracil (U)  <a href="#reference1">
            <sup>1</sup>
          </a>.
          Given our hypothesis that excessive T to U replacement might interfere with protein expression, 
          we opted to reduce the incubation time in our experiments to minimize this potential interference.
        </p>
        <center>
          <details>
            <summary id="Western_Blot_protocol"><mark><b>Speed-up Uracilation</b></mark></summary>
            <object data=
              "https://static.igem.wiki/teams/4888/wiki/prots/28-speed-up-uracilation-in-secondary-cultures.pdf"
              width="800"
              height="500"
              aria-label="western blot">
            </object>
          </details>
        </center>


        <h2 className="mt-20" id="References_h"> References </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul>
          <ol type="1">
            <li id="reference1">
              Uraciltion in bacteria article <br />
              <a href="https://journals.asm.org/doi/epdf/10.1128/jb.174.13.4450-4456.1992?src=getftr">
                Multiple Mutant of Escherichia coli Synthesizing Virtually Thymineless DNA during
                Limited Growth, JOURNAL OF BACTERIOLOGY,July 1992,p.4450-4456
              </a>
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
